import React, { useState, useEffect } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  SafeAreaView,
  Text,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../utils/colors';
import { typography } from '../utils/typography';
import { spacing } from '../utils/theme';

interface Notification {
  id: string;
  type: 'like' | 'repost' | 'reply' | 'follow' | 'mention';
  author: {
    handle: string;
    displayName: string;
    avatar?: string;
  };
  timestamp: string;
  read: boolean;
  postText?: string;
}

export const NotificationsScreen: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadNotifications = async () => {
    try {
      // TODO: Bluesky通知APIの実装
      // 現在はプレースホルダー
      setNotifications([]);
    } catch (error) {
      console.error('Load notifications error:', error);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadNotifications();
    setIsRefreshing(false);
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'like':
        return 'heart';
      case 'repost':
        return 'repeat';
      case 'reply':
        return 'chatbox';
      case 'follow':
        return 'person-add';
      case 'mention':
        return 'at';
      default:
        return 'notifications';
    }
  };

  const getNotificationText = (notification: Notification) => {
    const name = notification.author.displayName;
    switch (notification.type) {
      case 'like':
        return `${name}さんがあなたの投稿にいいねしました`;
      case 'repost':
        return `${name}さんがあなたの投稿をリポストしました`;
      case 'reply':
        return `${name}さんがあなたの投稿に返信しました`;
      case 'follow':
        return `${name}さんがあなたをフォローしました`;
      case 'mention':
        return `${name}さんがあなたをメンションしました`;
      default:
        return '通知';
    }
  };

  const renderNotification = ({ item }: { item: Notification }) => (
    <View
      style={[
        styles.notificationItem,
        !item.read && styles.notificationUnread,
      ]}
    >
      <View style={styles.iconContainer}>
        <Ionicons
          name={getNotificationIcon(item.type)}
          size={24}
          color={
            item.type === 'like'
              ? colors.error
              : colors.primary
          }
        />
      </View>
      <View style={styles.notificationContent}>
        <View style={styles.avatarPlaceholder} />
        <View style={styles.textContainer}>
          <Text style={styles.notificationText}>
            {getNotificationText(item)}
          </Text>
          {item.postText && (
            <Text style={styles.postText} numberOfLines={2}>
              {item.postText}
            </Text>
          )}
          <Text style={styles.timestamp}>{item.timestamp}</Text>
        </View>
      </View>
    </View>
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>通知</Text>
      </View>

      {notifications.length === 0 ? (
        <View style={styles.emptyState}>
          <Ionicons
            name="notifications-outline"
            size={64}
            color={colors.textSecondary}
          />
          <Text style={styles.emptyText}>通知はありません</Text>
        </View>
      ) : (
        <FlatList
          data={notifications}
          renderItem={renderNotification}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.list}
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={handleRefresh}
              tintColor={colors.primary}
            />
          }
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background,
  },
  headerTitle: {
    ...typography.h2,
    color: colors.text,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: spacing.xl * 2,
  },
  emptyText: {
    ...typography.body,
    color: colors.textSecondary,
    marginTop: spacing.md,
  },
  list: {
    paddingVertical: spacing.sm,
  },
  notificationItem: {
    flexDirection: 'row',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background,
  },
  notificationUnread: {
    backgroundColor: colors.backgroundSecondary,
  },
  iconContainer: {
    marginRight: spacing.sm,
    marginTop: spacing.xs,
  },
  notificationContent: {
    flex: 1,
    flexDirection: 'row',
  },
  avatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.backgroundSecondary,
    marginRight: spacing.sm,
  },
  textContainer: {
    flex: 1,
  },
  notificationText: {
    ...typography.body,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  postText: {
    ...typography.caption,
    color: colors.textSecondary,
    marginTop: spacing.xs,
    marginBottom: spacing.xs,
  },
  timestamp: {
    ...typography.caption,
    color: colors.textSecondary,
  },
});
